import { Notification as BaseNotification } from "@screencloud/screencloud-ui-components";
import { isValidElement, ReactNode } from "react";
import { RawIntlProvider } from "react-intl";
import { getGlobalIntl } from "src/utils/globalIntl";

type NotificationApi = typeof BaseNotification;
type NotificationArgs = Parameters<NotificationApi["success"]>[0];

/**
 * ui-components' Notification api renders each notification into its own
 * React root on document.body, outside the app's <IntlProvider>. react-intl
 * v3+ components throw without a provider in their ancestry, so re-provide
 * the app's intl around every React-element arg. Import Notification from
 * here instead of from ui-components.
 */
const withIntl = (node: ReactNode): ReactNode =>
  isValidElement(node) ? (
    <RawIntlProvider value={getGlobalIntl()}>{node}</RawIntlProvider>
  ) : (
    node
  );

const withAppIntl = (args: NotificationArgs): NotificationArgs => ({
  ...args,
  title: withIntl(args.title),
  message: withIntl(args.message),
  thumbnail: withIntl(args.thumbnail),
  expandableContent: withIntl(args.expandableContent),
});

export const Notification: NotificationApi = {
  success: (args) => BaseNotification.success(withAppIntl(args)),
  info: (args) => BaseNotification.info(withAppIntl(args)),
  warning: (args) => BaseNotification.warning(withAppIntl(args)),
  error: (args) => BaseNotification.error(withAppIntl(args)),
};
